import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { slotIdsForKind } from "@spectree/shared";
import type {
  FogbugzBugBarResult,
  FogbugzFormulatorCoverage,
  FogbugzFormulatorKind,
  FogbugzFormulatorRouting,
  FogbugzFormulatorSubmitResult,
  FogbugzFormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { BugBarPanel, belowBugBarFrom, bugBarBlocks } from "@/components/fogbugz/bug-bar-panel";
import { IntentReview } from "@/components/fogbugz/intent-review";
import { Button } from "@/components/ui/button";
import { useRoutingOptions, type FogbugzPriorityOption, type InterviewCoverage, type InterviewRouting } from "@/hooks/queries/use-fogbugz";
import {
  fetchToolboxInterview,
  fogbugzToolboxInterviewKeys,
  useSaveToolboxInterviewIntent,
  useSubmitToolboxInterview,
  useToolboxInterviewDuplicates,
  useToolboxInterviewReview,
  type ToolboxInterviewDuplicates,
  type ToolboxInterviewSession,
  type ToolboxSubmitFile,
} from "@/hooks/queries/use-fogbugz-toolbox-interview";
import { clearAcceptedSummaries } from "@/lib/toolbox/fogbugz-formulator";
import { safeHttpUrl } from "@/lib/safe-url";

/**
 * The website review editor's coverage shape from the Toolbox's coverage
 * (which names only what is still owed). The editor re-parses the text
 * itself; this only carries the kind and the owed slots. Pure; exported for tests.
 */
export function editorCoverage(kind: FogbugzFormulatorKind, coverage: FogbugzFormulatorCoverage): InterviewCoverage {
  const remaining = new Set([...coverage.requiredRemaining, ...coverage.optionalRemaining]);
  const all = slotIdsForKind(kind);
  return {
    kind,
    filled: all.filter((id) => !remaining.has(id)),
    unknown: [],
    missing: [...coverage.requiredRemaining],
    requiredRemaining: [...coverage.requiredRemaining],
    optionalRemaining: [...coverage.optionalRemaining],
    isComplete: coverage.isComplete,
    totalSlots: all.length,
  };
}

/**
 * The routing as the review editor's read-only summary shows it: the same
 * fields, without the ones the person left unset. Pure; exported for tests.
 */
export function editorRouting(
  routing: FogbugzFormulatorRouting,
  priorities: readonly FogbugzPriorityOption[] = [],
): InterviewRouting {
  const out: InterviewRouting = { ixProject: routing.ixProject, sProject: routing.sProject };
  for (const [key, value] of Object.entries(routing)) {
    if (value !== undefined) (out as unknown as Record<string, unknown>)[key] = value;
  }
  // The Toolbox routing carries the priority id only; name it from the FogBugz list the routing screen used.
  const priority = priorities.find((option) => option.ixPriority === routing.ixPriority);
  if (priority) out.sPriority = priority.sPriority;
  return out;
}

function Notice({ tone, testId, children }: { tone: "error" | "ok"; testId: string; children: ReactNode }) {
  const Icon = tone === "error" ? AlertCircle : CheckCircle2;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      data-testid={testId}
      className={
        tone === "error"
          ? "flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
          : "flex items-start gap-2 rounded-lg border border-green-500/20 bg-green-500/10 p-3 text-sm"
      }
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="flex flex-1 flex-col gap-1">{children}</div>
    </div>
  );
}

/** Possible duplicate open cases: a warning, never blocking. */
function Duplicates({
  isLoading,
  isError,
  data,
}: {
  isLoading: boolean;
  isError: boolean;
  data: ToolboxInterviewDuplicates | undefined;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground" role="status">Looking for open cases like this one…</p>;
  }
  if (isError || data?.unavailable) {
    return (
      <p className="rounded-lg border border-white/[0.08] p-3 text-sm text-muted-foreground" data-testid="fogbugz-formulator-dedupe-unavailable">
        The check for similar open cases did not run. You can still submit.
      </p>
    );
  }
  const duplicates = data?.duplicates ?? [];
  if (duplicates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="fogbugz-formulator-dedupe-none">
        No similar open cases found.
      </p>
    );
  }
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3"
      aria-labelledby="fogbugz-formulator-duplicates"
      data-testid="fogbugz-formulator-duplicates"
    >
      <h3 id="fogbugz-formulator-duplicates" className="text-sm font-medium text-yellow-500">
        {duplicates.length === 1 ? "1 open case may already describe this" : `${String(duplicates.length)} open cases may already describe this`}
      </h3>
      <p className="text-xs text-muted-foreground">Check them before you submit. You can still file yours.</p>
      <ul className="flex flex-col gap-2">
        {duplicates.map((item) => {
          const href = safeHttpUrl(item.url);
          return (
            <li key={item.caseId} className="flex items-start justify-between gap-3 text-sm" data-testid="fogbugz-formulator-duplicate">
              <span className="min-w-0 flex-1">
                <span className="font-mono text-primary">#{item.caseId}</span> {item.title}{" "}
                <span className="text-xs text-muted-foreground">({item.status})</span>
                {item.snippet && <span className="block text-xs text-muted-foreground line-clamp-2">{item.snippet}</span>}
              </span>
              {href && (
                <a href={href} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs text-primary hover:underline">
                  View in FogBugz
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export interface SubmitStepProps {
  /** The Bug bar on the saved report; Submit stays disabled while a bug case is below it. */
  bugBar: FogbugzBugBarResult | null;
  duplicates: { isLoading: boolean; isError: boolean; data: ToolboxInterviewDuplicates | undefined };
  /** What goes with the case (screenshots and event logs), read-only. */
  attachments?: ReactNode;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: () => void;
}

/**
 * The last step before filing: possible duplicates (a warning only), the Bug
 * bar, the files that go with the case, and Submit, which stays disabled
 * while a bug case is below the bar. Exported for tests.
 */
export function SubmitStep({ bugBar, duplicates, attachments, submitting, error, onBack, onSubmit }: SubmitStepProps) {
  const blocked = bugBarBlocks(bugBar);
  return (
    <div className="flex flex-col gap-4" data-testid="fogbugz-formulator-submit-step">
      <Duplicates {...duplicates} />
      {bugBar && (
        <BugBarPanel
          bugBar={bugBar}
          hint={blocked ? "Go back to your report, add what is listed, and submit again." : undefined}
        />
      )}
      {attachments}
      {error && (
        <Notice tone="error" testId="fogbugz-formulator-submit-error">
          <span>{error}</span>
        </Notice>
      )}
      <div className="flex justify-end gap-3 border-t border-white/[0.08] pt-4">
        <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
          Back to the report
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={submitting || blocked}
          title={blocked ? "This bug case is not ready to file yet. Add what is listed above first." : undefined}
          data-testid="fogbugz-formulator-submit"
        >
          {submitting ? "Filing…" : "Submit to FogBugz"}
        </Button>
      </div>
    </div>
  );
}

/** Plain words for a failed file read-back. */
class AttachmentReadError extends Error {}

/**
 * Read every kept file back from the Toolbox for the multipart submit (works
 * after an app restart: the files are on disk, not in page memory).
 * Exported for tests.
 */
export async function readKeptFiles(
  bridge: FogbugzFormulatorToolsetBridge,
  sessionId: string,
): Promise<{ screenshots: ToolboxSubmitFile[]; eventLogs: ToolboxSubmitFile[] }> {
  const listed = await bridge.listAttachments({ sessionId });
  if (!listed.ok) throw new AttachmentReadError(listed.message || "The files for this report could not be read on this computer.");
  const screenshots: ToolboxSubmitFile[] = [];
  const eventLogs: ToolboxSubmitFile[] = [];
  for (const item of listed.attachments) {
    const read = await bridge.readAttachment({ sessionId, name: item.name });
    if (!read.ok) throw new AttachmentReadError(read.message || `${item.name} could not be read on this computer. Add it again.`);
    (read.kind === "image" ? screenshots : eventLogs).push({ name: read.name, bytes: read.bytes });
  }
  return { screenshots, eventLogs };
}

export interface ReviewStepProps {
  bridge: FogbugzFormulatorToolsetBridge;
  session: ToolboxInterviewSession;
  /** Summaries the person accepted, by event-log file name. */
  summaries: Readonly<Record<string, string>>;
  /** The files that go with the case, read-only. */
  attachments?: ReactNode;
  onBack: () => void;
}

/**
 * Review and submit of the Toolbox FogBugz Formulator (epic PI-SPEC-SW-E309,
 * feature PI-SPEC-SW-1620; ER-1665 Resolved Decisions 4 and 4b, decision 7).
 *
 * 1. Edit: the intent.md as SpecTree would file it (`GET /:id/review`), in the
 *    website's review editor with the same Bug-bar panel. Continue saves the
 *    edit (`PUT /:id/intent`) and re-checks the bar; a bug case below it stays
 *    here with each missing rule named.
 * 2. Submit: possible duplicates (`POST /:id/dedupe`, a warning only), the Bug
 *    bar (Submit disabled below it), then one multipart `POST /:id/submit`
 *    with the files read back from the Toolbox and the reviewed summaries.
 *    A `BELOW_BUG_BAR` refusal lists the missing rules; a filed case shows
 *    its number and link, and the files on this computer are forgotten.
 */
export function ReviewStep({ bridge, session, summaries, attachments, onBack }: ReviewStepProps) {
  const routingOptions = useRoutingOptions();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"edit" | "submit">("edit");
  const [saved, setSaved] = useState<{ intentMarkdown: string; version: number; bugBar: FogbugzBugBarResult } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkedBugBar, setCheckedBugBar] = useState<FogbugzBugBarResult | null>(null);
  const [reading, setReading] = useState(false);
  const [filed, setFiled] = useState<FogbugzFormulatorSubmitResult | null>(null);
  const review = useToolboxInterviewReview(step === "edit" && filed === null ? session.id : null);
  const save = useSaveToolboxInterviewIntent();
  const duplicates = useToolboxInterviewDuplicates(step === "submit" ? session.id : null);
  const submit = useSubmitToolboxInterview();

  if (filed) {
    const href = safeHttpUrl(filed.caseUrl);
    return (
      <Notice tone="ok" testId="fogbugz-formulator-filed-now">
        <span className="font-medium">Filed as FogBugz case #{filed.caseId}</span>
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
            Open case #{filed.caseId} in FogBugz
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </Notice>
    );
  }

  const onContinue = (intentMarkdown: string) => {
    const version = saved?.version ?? review.data?.version;
    if (version === undefined) return;
    setSaveError(null);
    save.mutate(
      { sessionId: session.id, intentMarkdown, version },
      {
        onSuccess: (response) => {
          const result = response.data;
          setCheckedBugBar(result.bugBar);
          setSaved({ intentMarkdown: result.intentMarkdown, version: result.version, bugBar: result.bugBar });
          if (!result.coverage.isComplete) {
            setSaveError("The report is still missing something. Fill in what is listed, then continue.");
            return;
          }
          // A bug case below the bar stays here; the panel names each missing rule.
          if (bugBarBlocks(result.bugBar)) return;
          setStep("submit");
        },
        onError: (error) => {
          setSaveError(error instanceof Error && error.message ? error.message : "Your edits could not be saved.");
        },
      },
    );
  };

  const onSubmit = async () => {
    if (!saved || submit.isPending || reading) return;
    setSubmitError(null);
    setReading(true);
    let files: Awaited<ReturnType<typeof readKeptFiles>>;
    try {
      files = await readKeptFiles(bridge, session.id);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "The files for this report could not be read.");
      return;
    } finally {
      setReading(false);
    }
    const logNames = new Set(files.eventLogs.map((file) => file.name));
    const evtxSummaries = Object.entries(summaries)
      .filter(([fileName]) => logNames.has(fileName))
      .map(([fileName, sections]) => ({ fileName, sections }));
    submit.mutate(
      {
        sessionId: session.id,
        metadata: { version: saved.version, intentMarkdown: saved.intentMarkdown, evtxSummaries },
        screenshots: files.screenshots,
        eventLogs: files.eventLogs,
      },
      {
        onSuccess: (response) => {
          setFiled(response.data);
          clearAcceptedSummaries(session.id);
          // The case has the files now; drop them from this computer.
          void bridge.forget({ sessionId: session.id }).catch(() => undefined);
        },
        onError: (error) => {
          const refusal = belowBugBarFrom(error);
          if (refusal) setCheckedBugBar(refusal);
          setSubmitError(error instanceof Error && error.message ? error.message : "The case could not be filed. Try again.");
          // A failed submit moves the session's version on the server (the claim
          // and its release), so read it again: Submit again, or Back and
          // Continue, would otherwise be refused as changed in another window.
          void queryClient
            .fetchQuery({
              queryKey: fogbugzToolboxInterviewKeys.session(session.id),
              queryFn: () => fetchToolboxInterview(session.id),
            })
            .then((fresh) => {
              setSaved((current) => (current ? { ...current, version: fresh.data.version } : current));
            })
            .catch(() => undefined);
        },
      },
    );
  };

  if (step === "submit") {
    return (
      <SubmitStep
        bugBar={checkedBugBar ?? saved?.bugBar ?? null}
        duplicates={{ isLoading: duplicates.isLoading, isError: duplicates.isError, data: duplicates.data }}
        attachments={attachments}
        submitting={submit.isPending || reading}
        error={submitError}
        onBack={() => {
          setSubmitError(null);
          setStep("edit");
        }}
        onSubmit={() => {
          void onSubmit();
        }}
      />
    );
  }

  if (review.isLoading) {
    return <p className="text-sm text-muted-foreground" role="status">Loading your report…</p>;
  }
  if (review.isError || !review.data) {
    return (
      <Notice tone="error" testId="fogbugz-formulator-review-error">
        <span>{review.error instanceof Error && review.error.message ? review.error.message : "Your report could not be loaded."}</span>
      </Notice>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="fogbugz-formulator-review">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Review your report</h2>
        <p className="text-sm text-muted-foreground">
          This is the record that will be filed. Correct anything that is wrong or incomplete: you are editing the
          document itself.
        </p>
      </div>
      <IntentReview
        initialText={saved?.intentMarkdown ?? review.data.intentMarkdown}
        serverCoverage={editorCoverage(session.kind, review.data.coverage)}
        bugBar={checkedBugBar ?? review.data.bugBar}
        isSaving={save.isPending}
        saveError={saveError}
        routing={editorRouting(session.routing, routingOptions.data?.data.priorities)}
        onBack={onBack}
        backLabel="Back to the interview"
        onContinue={onContinue}
        continueLabel="Continue to submit"
      />
    </div>
  );
}
